// Barre d'adresse du panneau Navigateur, portee de browser-address-bar.tsx de l'ancienne app
// Pirouette. Toujours interactive : naviguer n'est pas « prendre la main ». Au repos elle affiche
// l'adresse (cadenas si https, domaine en gras, chemin attenue, titre en infobulle) ; au clic
// elle devient un champ avec l'adresse complete selectionnee. Le chevron deroule les pages vues
// par l'ASSISTANT pendant la conversation, rien n'est garde.
import type { VisiteNavigateur } from "@app/lib/compta/navigateur/adresse";
import {
  ancienneteRelative,
  formaterAdresse,
  hoteDe,
  normaliserAdresse,
} from "@app/lib/compta/navigateur/adresse";
import { LIBELLES_NAVIGATEUR as L } from "@app/lib/compta/navigateur/libelles";
import { classNames } from "@app/lib/utils";
import {
  ChevronDown,
  Globe01,
  Icon,
  LinkExternal01,
  Lock01,
  Spinner,
} from "@dust-tt/sparkle";
import { useEffect, useRef, useState } from "react";

const TAILLE_HISTORIQUE = 10;

export function BarreAdresseNavigateur({
  url,
  titre,
  chargement,
  assistantActif,
  visites,
  surNavigation,
}: {
  url: string;
  titre: string | null;
  chargement: boolean;
  /** L'assistant vient d'agir : l'icone passe a l'ambre, la ou se pose l'oeil. */
  assistantActif: boolean;
  visites: VisiteNavigateur[];
  surNavigation: (url: string) => void;
}) {
  const [edition, setEdition] = useState(false);
  const [brouillon, setBrouillon] = useState("");
  const [invalide, setInvalide] = useState(false);
  const [historiqueOuvert, setHistoriqueOuvert] = useState(false);
  const champ = useRef<HTMLInputElement>(null);
  const racine = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (edition) {
      champ.current?.focus();
      champ.current?.select();
    }
  }, [edition]);

  useEffect(() => {
    if (!historiqueOuvert) {
      return;
    }
    const dehors = (e: MouseEvent) => {
      if (racine.current && !racine.current.contains(e.target as Node)) {
        setHistoriqueOuvert(false);
      }
    };
    const echap = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setHistoriqueOuvert(false);
      }
    };
    document.addEventListener("mousedown", dehors);
    document.addEventListener("keydown", echap);
    return () => {
      document.removeEventListener("mousedown", dehors);
      document.removeEventListener("keydown", echap);
    };
  }, [historiqueOuvert]);

  const commencer = () => {
    setBrouillon(url);
    setInvalide(false);
    setHistoriqueOuvert(false);
    setEdition(true);
  };
  const arreter = () => {
    setEdition(false);
    setInvalide(false);
  };
  const valider = () => {
    const lu = normaliserAdresse(brouillon);
    if (!lu.ok) {
      if (lu.raison === "vide") {
        arreter();
      } else {
        setInvalide(true);
      }
      return;
    }
    surNavigation(lu.url);
    arreter();
  };

  const affichage = formaterAdresse(url);
  const historique = visites.slice(-TAILLE_HISTORIQUE).reverse();
  const maintenant = Date.now();
  const teinte = assistantActif
    ? "text-warning-500"
    : "text-muted-foreground dark:text-muted-foreground-night";

  return (
    <div ref={racine} className="relative flex min-w-0 flex-1 flex-col gap-0.5">
      <div className="flex min-w-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 dark:border-border-night dark:bg-background-night">
        {chargement ? (
          <Spinner size="xs" />
        ) : (
          <Icon
            visual={affichage.securisee ? Lock01 : Globe01}
            size="2xs"
            className={teinte}
          />
        )}
        {edition ? (
          <input
            ref={champ}
            className="min-w-0 flex-1 border-none bg-transparent p-0 text-xs text-foreground outline-none focus:ring-0 dark:text-foreground-night"
            value={brouillon}
            placeholder={L.adressePlaceholder}
            aria-label={L.modifierAdresse}
            aria-invalid={invalide || undefined}
            onChange={(e) => {
              setBrouillon(e.target.value);
              setInvalide(false);
            }}
            onBlur={arreter}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                valider();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                arreter();
              }
            }}
          />
        ) : (
          <button
            type="button"
            onClick={commencer}
            title={titre ?? (url || undefined)}
            aria-label={L.modifierAdresse}
            className="min-w-0 flex-1 truncate text-left text-xs"
          >
            {url ? (
              <>
                <span className="font-medium text-foreground dark:text-foreground-night">
                  {affichage.hote}
                </span>
                <span className="text-muted-foreground dark:text-muted-foreground-night">
                  {affichage.chemin}
                </span>
              </>
            ) : (
              <span className="text-muted-foreground dark:text-muted-foreground-night">
                {L.adressePlaceholder}
              </span>
            )}
          </button>
        )}
        {historique.length > 0 && (
          <button
            type="button"
            onClick={() => setHistoriqueOuvert((o) => !o)}
            title={L.historique}
            aria-label={L.historique}
            aria-expanded={historiqueOuvert}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground dark:text-muted-foreground-night dark:hover:text-foreground-night"
          >
            <Icon
              visual={ChevronDown}
              size="2xs"
              className={classNames(
                "transition-transform",
                historiqueOuvert ? "rotate-180" : ""
              )}
            />
          </button>
        )}
        <button
          type="button"
          disabled={!url}
          onClick={() => {
            if (url) {
              window.open(url, "_blank", "noopener");
            }
          }}
          title={L.ouvrirAilleurs}
          aria-label={L.ouvrirAilleurs}
          className="shrink-0 rounded p-0.5 text-muted-foreground enabled:hover:text-foreground disabled:opacity-30 dark:text-muted-foreground-night dark:enabled:hover:text-foreground-night"
        >
          <Icon visual={LinkExternal01} size="2xs" />
        </button>
      </div>
      {invalide && (
        <div className="px-3 text-[11px] text-warning-500">
          {L.adresseInvalide}
        </div>
      )}
      {historiqueOuvert && (
        <div className="absolute top-full left-0 z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-border bg-background p-1 shadow-md dark:border-border-night dark:bg-background-night">
          <div className="px-2 py-1 text-[10px] font-medium tracking-wide text-muted-foreground uppercase dark:text-muted-foreground-night">
            {L.historique}
          </div>
          {historique.map((v) => (
            <button
              key={`${v.url}-${v.a}`}
              type="button"
              onClick={() => {
                setHistoriqueOuvert(false);
                surNavigation(v.url);
              }}
              className="flex w-full min-w-0 flex-col items-start gap-0.5 rounded-lg px-2 py-1.5 text-left hover:bg-muted-background dark:hover:bg-muted-background-night"
            >
              <span className="w-full truncate text-xs text-foreground dark:text-foreground-night">
                {v.titre ?? (hoteDe(v.url) || v.url)}
              </span>
              <span className="flex w-full min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground dark:text-muted-foreground-night">
                <span className="truncate">{hoteDe(v.url) || v.url}</span>
                <span className="shrink-0">
                  · {ancienneteRelative(v.a, maintenant)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
